import { Search, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useCompanySearch } from './CompanyPicker'
import { companyAnalysisHref, companyMeta } from './companyIdentity'
import type { SecuritySearchResult } from '../api/types'
import { globalScope } from '../hotkeys/globalScopes'
import { useHotkeyText } from '../hotkeys/useHotkeyText'
import { useHotkeyScope } from '../hotkeys/useHotkeyScope'

export function GlobalCompanySearch() {
  const navigate = useNavigate()
  const input = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const id = useId()
  const list = useRef<HTMLDivElement>(null)
  const search = useCompanySearch(query)
  // "/" jumps to company search from anywhere in the workspace.
  useHotkeyScope(globalScope, { search: () => { input.current?.focus(); input.current?.select() } })
  const searchKey = useHotkeyText(globalScope.byId.search)
  const choose = (company: SecuritySearchResult) => {
    setQuery('')
    setOpen(false)
    input.current?.blur()
    navigate(companyAnalysisHref(company))
  }
  const results = search.data?.results ?? []
  const activeIndex = Math.min(active, Math.max(0, results.length - 1))
  const showResults = open
  const searching = query.trim().length >= 2
  useEffect(() => {
    if (showResults) list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' })
  }, [showResults, activeIndex])
  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const value = query.trim()
    if (value.length < 2) return
    const current = search.data?.results ?? []
    const found = current.length ? current : (await search.refetch()).data?.results ?? []
    // A response to an earlier query must never open the wrong company.
    if (input.current?.value.trim() !== value || document.activeElement !== input.current) return
    const chosen = found[current.length ? activeIndex : 0]
    if (chosen) choose(chosen)
  }
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) { if (event.key === 'Enter') event.preventDefault(); return }
    if (event.key === 'Escape') {
      if (open) { event.preventDefault(); event.stopPropagation(); setOpen(false) }
      else input.current?.blur()
      return
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    setOpen(true)
    if (!results.length) return
    const step = event.key === 'ArrowDown' ? 1 : -1
    setActive(!open ? (step === 1 ? 0 : results.length - 1) : (activeIndex + step + results.length) % results.length)
  }
  return (
    <div
      className="global-search-wrap"
      onFocus={() => setOpen(true)}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false)
      }}
    >
      <form className="global-search" role="search" onSubmit={event => void submit(event)}>
        <Search aria-hidden="true" />
        <input
          ref={input}
          role="combobox"
          aria-label="Search companies"
          aria-autocomplete="list"
          value={query}
          onChange={event => { setQuery(event.target.value); setOpen(true); setActive(0) }}
          onKeyDown={onKeyDown}
          placeholder="Find a company: name, ticker, or code"
          autoComplete="off"
          aria-expanded={showResults}
          aria-controls={`${id}-results`}
          aria-activedescendant={showResults && searching && results.length ? `${id}-result-${activeIndex}` : undefined}
        />
        {query && <button className="icon-button global-search-clear" type="button" aria-label="Clear company search" onClick={() => { setQuery(''); setActive(0); input.current?.focus() }}><X /></button>}
        <kbd title={`Press ${searchKey} to search from anywhere`}>{searchKey}</kbd>
      </form>
      {showResults && (
        <div id={`${id}-results`} className="global-search-results" role="listbox" aria-label="Company suggestions" aria-busy={searching && search.isFetching} ref={list}>
          {!searching && <span className="global-search-status" role="status">Search all countries by name, ticker, or company code. Type at least 2 characters.</span>}
          {searching && search.isLoading && <span className="global-search-status" role="status">Searching…</span>}
          {searching && search.isError && <div className="company-search-error" role="alert">Company search is unavailable.<button type="button" className="text-button" onMouseDown={event => event.preventDefault()} onClick={() => void search.refetch()}>Try again</button></div>}
          {searching && !search.isLoading && !search.isError && results.length === 0 && <span className="global-search-status" role="status">No companies found. Try another name, ticker, or company code.</span>}
          {searching && results.map((company, index) => (
            <button
              type="button"
              role="option"
              id={`${id}-result-${index}`}
              aria-selected={index === activeIndex}
              className={index === activeIndex ? 'active' : undefined}
              key={`${company.company_code ?? 'ticker'}-${company.ticker}-${company.company_name}`}
              tabIndex={-1}
              onMouseDown={event => event.preventDefault()}
              onMouseEnter={() => setActive(index)}
              onClick={() => choose(company)}
            >
              <strong>{company.company_name || company.ticker || company.company_code}</strong>
              <small>{companyMeta(company)}</small>
            </button>
          ))}
          {searching && results.length > 0 && <span className="global-search-hint"><kbd>↑</kbd><kbd>↓</kbd> choose · <kbd>Enter</kbd> open · <kbd>Esc</kbd> close</span>}
        </div>
      )}
    </div>
  )
}
